import { translate } from '@utils/i18n';

import NameCell from './nested/NameCell';
import ActionsCell from './nested/ActionsCell';

export const CACHE_KEY = 'kendo/settings/tags/index';

export const NAMESPACE = 'features.settings_tags';

export const t = (key, params = {}) => translate(key, { namespace: NAMESPACE, ...params });

export const INITIAL_PAGE = { skip: 0, take: 25 };

export const PAGE_SIZES = [25, 50, 100];

export const INITIAL_SORT = [{ field: 'total', dir: 'desc' }];

export const INITIAL_FILTERS = {
  values: {
    name: '',
    taggableType: '',
    unusedOnly: ''
  },
  // FilterPanel renders ONLY filters with `active: true` and has no chooser for
  // inactive ones (see its index.jsx line 34), so an inactive filter is simply
  // invisible — it never reaches the UI at all.
  active: {
    name: true,
    taggableType: true,
    unusedOnly: true
  },
  operators: {
    name: 'contains'
  }
};

// Mirrors Kendo::TagsDatatable::TAGGABLE_TYPES. The `field` values match the
// SELECT aliases the datatable builds (`inspection_count` -> `inspectionCount`),
// so a change there has to be made in both places.
export const TAGGABLE_TYPE_COLUMNS = [
  { field: 'inspectionCount', type: 'Inspection' },
  { field: 'buildingCount', type: 'Building' },
  { field: 'workOrderCount', type: 'WorkOrder' },
  { field: 'inspectionSeriesCount', type: 'InspectionSeries' },
  { field: 'deficiencyCount', type: 'Deficiency' },
  { field: 'accountCount', type: 'Account' },
  { field: 'proposalCount', type: 'Proposal' }
];

export const ALL_COLUMNS = ['name', 'total', ...TAGGABLE_TYPE_COLUMNS.map((column) => column.field), 'actions'];

// The tag name is the only way to tell one row from another, so it cannot be
// hidden from the column menu.
export const REQUIRED_COLUMNS = ['name'];

// Everything but the row-action pseudo column; Kendo::TagsDatatable's
// ALLOWED_SORT_FIELDS covers exactly these.
export const SORTABLE_COLUMNS = ALL_COLUMNS.filter((id) => id !== 'actions');

const LABEL_KEYS = {
  name: 'table.headers.name',
  total: 'table.headers.total',
  actions: 'table.headers.actions',
  ...Object.fromEntries(
    TAGGABLE_TYPE_COLUMNS.map(({ field, type }) => [
      field,
      `table.headers.${type.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()}`
    ])
  )
};

const WIDTHS = { name: '260px', total: '110px', actions: '110px' };
const TYPE_COLUMN_WIDTH = '140px';

// No column is pinned, deliberately.
//
// Quimby's Table pins via columnType: 'primary' sticks a column to the left
// (TableStyles.scss gives it position: sticky + an opaque background so the
// scrolling cells do not show through), and 'action' sticks one to the right.
// On a table this wide — name, total and seven per-type counts — a pinned cell
// detaches and floats over the counts as they scroll past.
//
// Product asked for neither: "I don't want the sticky, that was super weird and
// annoying". The row now scrolls as one piece. Re-adding a columnType here is
// all it takes to bring pinning back if that judgement changes.

// Cells are read from context (canEdit + the drawer opener) rather than passed
// down: Table's renderer contract only supplies { item, dataKey, index }.
const RENDERERS = { name: NameCell, actions: ActionsCell };

export const columnLabel = (id) => t(LABEL_KEYS[id]);

// Built on demand rather than at module load so I18n is warm and a column's
// label reflects the request locale.
export function buildColumns(ids) {
  return ids.map((id) => ({
    dataKey: id,
    label: columnLabel(id),
    width: WIDTHS[id] || TYPE_COLUMN_WIDTH,
    renderer: RENDERERS[id]
  }));
}

export function buildColumnVisibilityItems(ids) {
  return ids.map((id) => ({
    key: id,
    label: columnLabel(id),
    required: REQUIRED_COLUMNS.includes(id)
  }));
}
